import React, { useEffect, useMemo, useRef, useState } from 'react';
import css from './CustomUserOnBoarding.module.css';
import uploadIcon from '../../assets/svg/uploadIcon.svg';
import { IoClose } from 'react-icons/io5';
import classNames from 'classnames';
import { ReactComponent as CheckMark } from '../../assets/svg/checkmark.svg';
import { ReactComponent as PdfIcon } from '../../assets/svg/pdfIcon.svg';
import { ReactComponent as Tick } from '../../assets/svg/Tick.svg';
import { ValidationError } from '../../components';

export default function UploadField({
  input,
  meta,
  label,
  required,
  intl,
  isPdf,
  variant = 'multiple',
  accept,
  imagePreviewClassName,
  imageClassName,
  removeClassName,
  uploadBoxClassName,
  onFileSelected,
  posterUrl,
  emptyIconSrc,
  uploadProgress,
  maxFiles,
  maxFilesError,
  validateFile,
}) {
  const fileInputRef = useRef(null);
  const previewMediaRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isPreviewLoaded, setIsPreviewLoaded] = useState(false);
  // Set when picked files are rejected before reaching the field value: either
  // they fail `validateFile`, or they would push a multi-file field past `maxFiles`
  const [pickError, setPickError] = useState(null);
  const showError = Boolean(meta?.touched && meta?.error);
  const showPreview = !showError;
  const showUploadProgress = typeof uploadProgress === 'number' && uploadProgress < 100;

  const imagePreviewWrapper = classNames(imagePreviewClassName || css.imagePreviewWrapper);
  const imagePreview = classNames(imageClassName || css.imagePreview);
  const removeBtn = classNames(removeClassName || css.removeBtn);

  const isSingleImage = variant === 'single-image';
  const isSingleVideo = variant === 'single-video';
  const isSingleValue = isSingleImage || isSingleVideo;
  const files = isSingleValue
    ? input.value
      ? [input.value]
      : []
    : Array.isArray(input.value)
    ? input.value
    : [];

  const value = isSingleValue ? files[0] : null;
  const isAtFileLimit = !isSingleValue && Boolean(maxFiles) && files.length >= maxFiles;

  // Recreating an object URL on every render would make the <video>/<img> src
  // change identity each time, forcing the browser to reload it (video preview
  // would restart/flicker whenever any other field on the form changes). Only
  // create a new one when the underlying file actually changes, and revoke it
  // when replaced/unmounted.
  const previewUrl = useMemo(() => {
    if (!value) return null;
    if (value instanceof File) return URL.createObjectURL(value);
    if (typeof value === 'string') return value;
    return null;
  }, [value]);

  useEffect(() => {
    return () => {
      if (previewUrl && value instanceof File) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl, value]);

  // Remote banner/video/photo URLs can take a moment to fetch - blur the
  // preview in behind a shimmer placeholder instead of popping in once
  // loaded. Reset whenever the underlying preview changes (new file picked,
  // or a different saved URL) so the transition plays again for it.
  useEffect(() => {
    setIsPreviewLoaded(false);

    // If the browser already has this resource cached, it can finish
    // loading (and fire `load`/`loadedmetadata`) synchronously while the DOM
    // node is being created - before React has attached the onLoad/
    // onLoadedMetadata handler below. That leaves isPreviewLoaded stuck at
    // false forever (shimmer never clears), which is exactly what happens on
    // a repeat visit once the browser has cached the image. By the time this
    // effect runs (after commit), the element already reflects the real
    // state, so check it directly instead of only relying on the event.
    const node = previewMediaRef.current;
    if (!node) return;
    if (node.tagName === 'VIDEO' ? node.readyState >= 1 : node.complete) {
      setIsPreviewLoaded(true);
    }
  }, [previewUrl]);

  const isPdfUploaded =
    value instanceof File
      ? isPdf
        ? value.type === 'application/pdf'
        : value.type === 'application/pdf' || /\.pdf$/i.test(value.name)
      : typeof value === 'string' && /\.pdf$/i.test(value);
  const shouldApplyPdfClass = isPdf && isSingleImage && files[0] && isPdfUploaded;

  const openPicker = () => {
    if (isAtFileLimit) return;
    fileInputRef.current.click();
  };

  const handleFiles = newFiles => {
    if (!newFiles || newFiles.length === 0) return;

    // Keep invalid files out of the field value entirely, so they can't linger
    // (hidden behind the error) and count towards `maxFiles`
    const picked = Array.from(newFiles);
    const rejectionErrors = validateFile ? picked.map(validateFile).filter(Boolean) : [];
    const accepted = validateFile ? picked.filter(file => !validateFile(file)) : picked;

    if (accepted.length === 0) {
      setPickError(rejectionErrors[0] || null);
    } else if (isSingleValue) {
      setPickError(null);
      input.onChange(accepted[0]);
      if (typeof input.onBlur === 'function') input.onBlur();
      onFileSelected?.(accepted[0]);
    } else if (maxFiles && files.length + accepted.length > maxFiles) {
      setPickError(maxFilesError || `You can upload up to ${maxFiles} files.`);
    } else {
      setPickError(rejectionErrors[0] || null);
      input.onChange([...files, ...accepted]);
      if (typeof input.onBlur === 'function') input.onBlur();
    }

    // Reset the input value so the same file can be re-selected after removal
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files?.length) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleRemove = index => e => {
    e.stopPropagation();
    if (isSingleValue) {
      // If there's a freshly-picked, not-yet-saved file, "remove" should undo
      // that pick by reverting to the field's actual initial value (usually
      // undefined) - otherwise the form would stay dirty even though nothing
      // was really changed. But if the current value IS already the saved
      // value (no pending pick), reverting to "initial" is a no-op - the user
      // is asking to actually delete existing media, so clear it to null.
      const isPendingPick = input.value instanceof File;
      input.onChange(isPendingPick ? meta?.initial : null);
      onFileSelected?.(null);
    } else {
      const updated = files.filter((_, i) => i !== index);
      setPickError(null);
      input.onChange(updated.length > 0 ? updated : meta?.initial);
    }
  };

  const renderPdfPreview = value => {
    const fileName =
      value instanceof File
        ? value.name
        : typeof value === 'string'
        ? value.split('/').pop()
        : 'PDF document';

    return (
      <div className={css.pdfPreviewWrapper}>
        <div className={css.pdfPreview}>
          <div className={css.pdfUpload}>
            <div className={css.pdfInfo}>
              <PdfIcon />
              <span className={css.pdfName}>{fileName}</span>
            </div>
            <Tick className={css.icon} />
          </div>

          <button
            type="button"
            className={css.removeBtn}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              input.onChange(meta?.initial);
              if (typeof input.onBlur === 'function') input.onBlur();
            }}
          >
            <svg
              width="10px"
              height="10px"
              viewBox="0 0 10 10"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g strokeWidth="1" fillRule="evenodd">
                <g transform="translate(-821.000000, -311.000000)">
                  <g transform="translate(809.000000, 299.000000)">
                    <path
                      d="M21.5833333,16.5833333 L17.4166667,16.5833333 L17.4166667,12.4170833 C17.4166667,12.1866667 17.2391667,12 17.00875,12 C16.77875,12 16.5920833,12.18625 16.5920833,12.41625 L16.5883333,16.5833333 L12.4166667,16.5833333 C12.18625,16.5833333 12,16.7695833 12,17 C12,17.23 12.18625,17.4166667 12.4166667,17.4166667 L16.5875,17.4166667 L16.5833333,21.5829167 C16.5829167,21.8129167 16.7691667,21.9995833 16.9991667,22 L16.9995833,22 C17.2295833,22 17.41625,21.81375 17.4166667,21.58375 L17.4166667,17.4166667 L21.5833333,17.4166667 C21.8133333,17.4166667 22,17.23 22,17 C22,16.7695833 21.8133333,16.5833333 21.5833333,16.5833333"
                      transform="translate(17.000000, 17.000000) rotate(-45.000000) translate(-17.000000, -17.000000) "
                    />
                  </g>
                </g>
              </g>
            </svg>
          </button>
        </div>

        {showUploadProgress && (
          <div className={css.pdfProgressTrack}>
            <div className={css.pdfProgressFill} style={{ width: `${uploadProgress}%` }} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={css.uploadWrapper}>
      <label className={css.uploadLabel}>
        {label} {required && <span style={{ color: 'red' }}>*</span>}
      </label>

      <input
        type="file"
        ref={fileInputRef}
        className={css.hiddenInput}
        multiple={!isSingleValue}
        accept={
          accept ??
          (typeof isPdf !== 'undefined' && isPdf
            ? 'application/pdf,.pdf'
            : isSingleImage
            ? 'image/*,application/pdf'
            : isSingleVideo
            ? 'video/*'
            : undefined)
        }
        onChange={e => handleFiles(e.target.files)}
      />

      {/* PDF preview rendered OUTSIDE the clickable upload box to avoid conflict */}
      {showPreview && isSingleImage && files[0] && isPdfUploaded ? (
        renderPdfPreview(files[0])
      ) : (
        <>
          {/* Upload box */}
          <div
            className={classNames(uploadBoxClassName || css.uploadBox, {
              [css.dragActive]: isDragOver,
              [css.hasError]: showError,
              [css.isPdf]: shouldApplyPdfClass,
              [css.uploadBoxDisabled]: isAtFileLimit,
            })}
            onClick={openPicker}
            onDrop={handleDrop}
            onDragOver={e => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDragEnter={e => {
              e.preventDefault();
              setIsDragOver(true);
            }}
          >
            {/* Single image/video preview */}
            {showPreview && isSingleValue && files[0] && !isPdfUploaded && !isPdf ? (
              <div
                className={classNames(imagePreviewWrapper, {
                  [css.previewWrapperLoading]: !isPreviewLoaded,
                })}
              >
                {previewUrl ? (
                  isSingleVideo ? (
                    <video
                      ref={previewMediaRef}
                      src={previewUrl}
                      poster={posterUrl || undefined}
                      className={classNames(imagePreview, {
                        [css.previewLoading]: !isPreviewLoaded,
                      })}
                      controls
                      preload="metadata"
                      onClick={e => e.stopPropagation()}
                      onLoadedMetadata={() => setIsPreviewLoaded(true)}
                      onError={() => setIsPreviewLoaded(true)}
                    />
                  ) : (
                    <img
                      ref={previewMediaRef}
                      src={previewUrl}
                      alt="Preview"
                      className={classNames(imagePreview, {
                        [css.previewLoading]: !isPreviewLoaded,
                      })}
                      onLoad={() => setIsPreviewLoaded(true)}
                      onError={() => setIsPreviewLoaded(true)}
                    />
                  )
                ) : null}
                <button type="button" className={removeBtn} onClick={handleRemove()}>
                  ✕
                </button>
              </div>
            ) : (
              <>
                <img src={emptyIconSrc || uploadIcon} alt="" className={css.uploadIcon} />

                {/* TEXT SWITCHES BASED ON DRAG STATE */}
                <div className={css.textDiv}>
                  <div className={css.uploadText}>
                    {isAtFileLimit
                      ? intl?.formatMessage({ id: 'UploadField.maxFilesReached' }, { maxFiles }) ??
                        `Maximum of ${maxFiles} files reached`
                      : isDragOver
                      ? intl?.formatMessage({ id: 'UploadField.dragConfirmText' }) ??
                        'Release to upload'
                      : intl?.formatMessage({ id: 'UploadField.uploadText' }) ?? 'Upload files'}
                  </div>

                  {!isDragOver && !isAtFileLimit && (
                    <div className={css.uploadSubText}>
                      {intl?.formatMessage({ id: 'UploadField.uploadSubText' }) ??
                        'Drag and drop files here'}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Multiple files list */}
      {showPreview && !isSingleValue && files.length > 0 && (
        <div className={css.fileList}>
          {files.map((file, index) => (
            <div key={`${file.name}-${index}`} className={css.fileListItem}>
              <div className={css.fileItem}>
                <span>{file.name}</span>
                <CheckMark className={css.successIcon} />
              </div>
              <button type="button" className={css.removeBtn} onClick={handleRemove(index)}>
                <IoClose />
              </button>
            </div>
          ))}
        </div>
      )}

      {meta ? <ValidationError fieldMeta={meta} /> : null}
      {pickError && !showError ? (
        <ValidationError fieldMeta={{ touched: true, error: pickError }} />
      ) : null}
    </div>
  );
}
